import "../App.css";
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDemoEmbedUrl } from './DemoPage';
import SearchComponent from './generalSearch/SearchComponent';
import PrivilegesComponent from './PrivilegesComponent';
import CategoryComponent from './category/CategoryComponent';
import InventoryWorkspace from "./inventory/InventoryWorkspace";
import ProfileComponent from "../components/profile/ProfileComponent";
import UnitsPanel from "./units/UnitsPanel";
import UserManagementPanel from "./user/UserManagementPanel";
import ImportExportPanel from "./ImportExportPanel";
import AnalyticsPanel from "./system/AnalyticsPanel";
import SettingsPanel from "./system/SettingsPanel";
import OutreachPanel from "./system/OutreachPanel";

interface MenuComponentProps {
    token: string;
    authorities: string[];
    onLogout: () => void;
}

type Panel =
    | "SearchComponent"
    | "PriviligesComponent"
    | "CategoryComponent"
    | "InventoryComponent"
    | "ProfileComponent"
    | "UnitsPanel"
    | "UserManagement"
    | "ImportExport"
    | "Analytics"
    | "Settings"
    | "Outreach";

const MenuComponent: React.FC<MenuComponentProps> = ({ token, authorities, onLogout }) => {
    const navigate = useNavigate();
    const demoAvailable = !!useDemoEmbedUrl();
    const isRoot = authorities.includes("ROLE_ROOT");
    const can = (authority: string) => isRoot || authorities.includes(authority);

    const canSearch        = can("GENERAL_SEARCH");
    const canCategory      = can("CATEGORY_READ");
    const canInventory     = can("STORE_INVENTORY_READ") || can("STORE_INVENTORY_CREATE");
    const canUnits         = can("CATEGORY_UPDATE") || can("CATEGORY_CREATE");
    const canPrivileges    = can("ASSIGN_ROLES") || can("CREATE_ACCOUNT");
    const canUserManage    = can("CREATE_ACCOUNT") || can("ASSIGN_ROLES");
    const canImportExport  = can("CATEGORY_UPDATE") || can("CATEGORY_CREATE");
    const canAnalytics     = can("ANALYTICS_READ");
    const canSettings      = isRoot || authorities.includes("ROLE_SYS_ADMIN");
    const canOutreach      = can("OUTREACH_MANAGE");

    // Pick the first panel the user has access to as default
    const defaultPanel = (): Panel => {
        if (canSearch)     return "SearchComponent";
        if (canInventory)  return "InventoryComponent";
        if (canCategory)   return "CategoryComponent";
        return "ProfileComponent";
    };

    const [activePanel, setActivePanel] = useState<Panel>(defaultPanel);

    // If authorities change after mount (e.g. token is refresh), reset to a valid panel
    useEffect(() => {
        setActivePanel(defaultPanel());
    }, [authorities]);

    const renderPanel = () => {
        switch (activePanel) {
            case "SearchComponent":
                return canSearch ? <SearchComponent token={token} /> : <AccessDenied />;
            case "CategoryComponent":
                return canCategory ? <CategoryComponent token={token} /> : <AccessDenied />;
            case "PriviligesComponent":
                return canPrivileges ? <PrivilegesComponent token={token} /> : <AccessDenied />;
            case "InventoryComponent":
                return canInventory ? <InventoryWorkspace token={token} /> : <AccessDenied />;
            case "ProfileComponent":
                return <ProfileComponent token={token} authorities={authorities} onAccountClosed={onLogout} />;
            case "UnitsPanel":
                return canUnits ? <UnitsPanel token={token} /> : <AccessDenied />;
            case "UserManagement":
                return canUserManage ? <UserManagementPanel token={token} authorities={authorities} /> : <AccessDenied />;
            case "ImportExport":
                return canImportExport ? <ImportExportPanel token={token} /> : <AccessDenied />;
            case "Analytics":
                return canAnalytics ? <AnalyticsPanel token={token} /> : <AccessDenied />;
            case "Settings":
                return canSettings ? <SettingsPanel token={token} /> : <AccessDenied />;
            case "Outreach":
                return canOutreach ? <OutreachPanel token={token} /> : <AccessDenied />;
        }
    };

    return (
        <>
            <nav className="menu">
                {canSearch && (
                    <button
                        onClick={() => setActivePanel("SearchComponent")}
                        className={activePanel === "SearchComponent" ? "active" : ""}
                    >
                        Search
                    </button>
                )}
                {canCategory && (
                    <button
                        onClick={() => setActivePanel("CategoryComponent")}
                        className={activePanel === "CategoryComponent" ? "active" : ""}
                    >
                        Category
                    </button>
                )}
                {canInventory && (
                    <button
                        onClick={() => setActivePanel("InventoryComponent")}
                        className={activePanel === "InventoryComponent" ? "active" : ""}
                    >
                        Listing
                    </button>
                )}
                {canUnits && (
                    <button
                        onClick={() => setActivePanel("UnitsPanel")}
                        className={activePanel === "UnitsPanel" ? "active" : ""}
                    >
                        Units
                    </button>
                )}
                {canUserManage && (
                    <button
                        onClick={() => setActivePanel("UserManagement")}
                        className={activePanel === "UserManagement" ? "active" : ""}
                    >
                        Users
                    </button>
                )}
                {canPrivileges && (
                    <button
                        onClick={() => setActivePanel("PriviligesComponent")}
                        className={activePanel === "PriviligesComponent" ? "active" : ""}
                    >
                        Privileges
                    </button>
                )}
                <button
                    onClick={() => setActivePanel("ProfileComponent")}
                    className={activePanel === "ProfileComponent" ? "active" : ""}
                >
                    Profile
                </button>
                {canImportExport && (
                    <button
                        onClick={() => setActivePanel("ImportExport")}
                        className={activePanel === "ImportExport" ? "active" : ""}
                    >
                        Import / Export
                    </button>
                )}
                {canAnalytics && (
                    <button
                        onClick={() => setActivePanel("Analytics")}
                        className={activePanel === "Analytics" ? "active" : ""}
                    >
                        Analytics
                    </button>
                )}
                {canSettings && (
                    <button
                        onClick={() => setActivePanel("Settings")}
                        className={activePanel === "Settings" ? "active" : ""}
                    >
                        Settings
                    </button>
                )}
                {canOutreach && (
                    <button
                        onClick={() => setActivePanel("Outreach")}
                        className={activePanel === "Outreach" ? "active" : ""}
                    >
                        Outreach
                    </button>
                )}
                {demoAvailable && (
                    <button onClick={() => navigate("/demo")}>
                        Demo
                    </button>
                )}
                <button onClick={onLogout} className="menu-logout-btn">
                    Logout
                </button>
            </nav>
            <main className="panel">{renderPanel()}</main>
        </>
    );
};

const AccessDenied: React.FC = () => (
    <div style={{ padding: "2rem", color: "var(--text-secondary)" }}>
        You do not have permission to access this page.
    </div>
);

export default MenuComponent;
